import {ChangeDetectorRef, ElementRef, Injectable, NgZone} from '@angular/core';
import {captchaConfigurations, CaptchaStatus, CaptchaType} from '@core/services/captcha/captcha';
import {BehaviorSubject, Subject} from 'rxjs';
import {LAZY_SCRIPT_NAME, LazyScriptLoaderService} from '@utils/services/lazy-script-loader/lazy-script-loader.service';

declare const grecaptcha: any;

declare global {
  interface Window {
    grecaptchaloaded: Function;
  }
}

/**
 * Captcha service responsible for communication with Captcha servers and handling of captcha state and results (e.g. tokens)
 */

@Injectable()
export class CaptchaService {
  _captchaStatus: CaptchaStatus = CaptchaStatus.Uninitialised;
  public captchaFulfilled: boolean = false;

  private _captchaType: CaptchaType;
  private widget: number = null;
  private container: ElementRef = null;
  private script = null;

  public onCaptchaLoaded = new Subject<boolean>();
  public onCaptchaResolutionChange = new BehaviorSubject<string>(null);

  constructor(private scriptLoader: LazyScriptLoaderService,
              private changeDetector: ChangeDetectorRef,
              private zone: NgZone) {}

  set captchaStatus(status: CaptchaStatus) {
    this._captchaStatus = status;
    this.captchaFulfilled = this._captchaType === CaptchaType.None || this._captchaStatus === CaptchaStatus.Resolved;
  }

  set captchaType(type: CaptchaType) {
    this._captchaType = CaptchaType.fromString(type);
    this.captchaStatus = this._captchaType === CaptchaType.None? CaptchaStatus.NotRequired : CaptchaStatus.Uninitialised;
  }

  public async loadCaptcha() {
    if (this._captchaStatus !== CaptchaStatus.Uninitialised || this._captchaType === CaptchaType.None) {
      return;
    }

    this.captchaStatus = CaptchaStatus.Loading;

    if (this.script) {
      this.__onCaptchaLoaded();
    } else {
      // function name is passed to script loaded below
      window.grecaptchaloaded = () => this.__onCaptchaLoaded();
      this.script = await this.scriptLoader.load(LAZY_SCRIPT_NAME.GOOGLE_RECAPTCHA_V2);
    }
  }

  private async onResolve(response: string) {
    if (response) {
      this.captchaStatus = CaptchaStatus.Resolved;
    }

    this.onCaptchaResolutionChange.next(response);
    this.changeDetector.detectChanges(); // ensure state changes are propagated to GUI elements
  }

  private __onCaptchaLoaded(): void {
    this.captchaStatus = CaptchaStatus.Pending;
    this.onCaptchaLoaded.next(true);
  }

  private async onExpire() {
    this.captchaStatus = CaptchaStatus.Pending;
    this.onCaptchaResolutionChange.next(null);
  }

  public reset(): void {
    if (this.widget != null) {
      this.zone.run(() => grecaptcha.reset(this.widget));
    }
  }

  public renderCaptcha(container: ElementRef) {
    if (grecaptcha && grecaptcha.render instanceof Function && this._captchaStatus === CaptchaStatus.Pending) {
      const config = captchaConfigurations[this._captchaType];
      config.callback = (response: string) => this.onResolve(response);
      config['expired-callback'] = () => this.onExpire();
      this.container = container;
      this.widget = grecaptcha.render(container.nativeElement, config);
    }
  }
}
